/**
 * Daily Micro-Practice (CRM web) — today's drill + 6-pillar 30-day trend.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, Play, Trophy, Flame, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function pillarColor(score) {
    if (score == null) return '#B8B8BE';
    if (score >= 85) return '#1DA063';
    if (score >= 70) return '#F2A423';
    return '#E5484D';
}

export default function MicroPractice() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [drill, setDrill] = useState(null);
    const [pillars, setPillars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [t, pt] = await Promise.all([
                    axios.get(`${API}/micro-practice/today`, { headers: getAuthHeader() }),
                    axios.get(`${API}/micro-practice/pillar-trend`, {
                        headers: getAuthHeader(),
                        params: { days: 30 },
                    }).catch(() => ({ data: { pillars: [] } })),
                ]);
                setDrill(t.data);
                setPillars(pt.data?.pillars || []);
            } catch (e) {
                toast.error('Failed to load daily practice');
            } finally {
                setLoading(false);
            }
        })();
    }, [getAuthHeader]);

    const startDrill = async () => {
        if (!drill?.avatar_id) {
            toast.error('No avatar available for today\'s drill');
            return;
        }
        setStarting(true);
        try {
            const res = await axios.post(`${API}/role-play/sessions`, {
                avatar_ids: [drill.avatar_id],
                difficulty: 'standard',
            }, { headers: getAuthHeader() });
            navigate(`/practice/role-play/live/${res.data.session_id}`);
        } catch (e) {
            toast.error('Failed to start drill');
            setStarting(false);
        }
    };

    if (loading || !drill) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Daily Micro-Practice
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        5-min drills personalised from your last calls
                    </p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700">
                    <Flame className="w-4 h-4" />
                    <span className="text-sm font-semibold">{drill.streak_days} day streak</span>
                </div>
            </div>

            {/* Today's drill hero */}
            <div
                className="rounded-2xl p-6 mb-8 border"
                style={{
                    background: 'linear-gradient(135deg, #F4F0FF, #FFFFFF)',
                    borderColor: '#D4C7FF',
                }}
            >
                <div className="flex items-center gap-2 mb-3 text-xs font-bold tracking-widest uppercase" style={{ color: '#4527A0' }}>
                    <Zap className="w-4 h-4" />
                    Today's drill · {drill.duration_minutes} min
                    <span className="ml-auto text-stone-400 font-normal normal-case tracking-normal">{drill.day}</span>
                </div>
                <h2 className="text-2xl font-bold tracking-tight leading-tight">{drill.topic}</h2>
                <p className="mt-2 text-stone-600 leading-relaxed">{drill.topic_reason}</p>
                <div className="mt-4 flex items-center gap-3">
                    <button
                        onClick={startDrill}
                        disabled={starting}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 disabled:opacity-50"
                    >
                        <Play className="w-4 h-4" />
                        {starting ? 'Starting…' : 'Start drill'}
                    </button>
                    {drill.avatar_name && (
                        <span className="text-xs text-stone-500">
                            Sparring partner: <b className="text-stone-800">{drill.avatar_name}</b>
                        </span>
                    )}
                </div>
            </div>

            {/* 6 pillars */}
            <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-stone-400" />
                <h2 className="text-xs font-bold tracking-widest uppercase text-stone-500">
                    Six pillars · Your 30-day avg
                </h2>
                <button onClick={() => navigate('/leaderboard')} className="ml-auto text-xs font-semibold text-violet-600 hover:text-violet-700">
                    See leaderboard →
                </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pillars.map(p => {
                    const color = pillarColor(p.score);
                    return (
                        <div key={p.pillar} className="bg-white border border-stone-200 rounded-xl p-4">
                            <div className="flex items-center mb-2">
                                <span className="text-sm font-bold">{p.pillar}</span>
                                <div className="flex-1" />
                                {p.trend !== 0 && (
                                    <span
                                        className="text-xs font-semibold mr-2"
                                        style={{ color: p.trend > 0 ? '#1DA063' : '#E5484D' }}
                                    >
                                        {p.trend > 0 ? '+' : ''}{p.trend}
                                    </span>
                                )}
                                <span className="text-base font-bold tracking-tight" style={{ color }}>
                                    {p.score == null ? '—' : `${p.score}%`}
                                </span>
                            </div>
                            <div className="h-1 rounded-full bg-stone-100 overflow-hidden">
                                <div
                                    className="h-full rounded-full"
                                    style={{ width: `${p.score || 0}%`, background: color }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
